import { supabase } from "@/integrations/supabase/client";

interface LogDetailedEntryParams {
  userId: string;
  entryDate: string; // ISO date format YYYY-MM-DD
  metricKey: string;
  entryText: string;
}

/**
 * Logs a detailed entry for a metric on a specific date.
 * Silently handles errors (up to 25 entries per day per metric).
 */
export async function logDetailedEntry({
  userId,
  entryDate,
  metricKey,
  entryText,
}: LogDetailedEntryParams): Promise<void> {
  try {
    const { error } = await supabase
      .from("detailed_entries")
      .insert({
        user_id: userId,
        entry_date: entryDate,
        metric_key: metricKey,
        entry_text: entryText,
        entry_time: new Date().toISOString(),
      });

    if (error) {
      // Silently log the error without disrupting user experience
      console.error("[DetailedEntryLogger] Failed to log entry:", error);
    }
  } catch (err) {
    // Silently catch any exceptions
    console.error("[DetailedEntryLogger] Exception logging entry:", err);
  }
}

/**
 * Formats time in 12-hour format with AM/PM
 */
function formatTime(date: Date): string {
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/**
 * Logs a manual quick entry (non-AI)
 * Format: "You logged VALUE METRIC_NAME at TIME"
 */
export async function logManualQuickEntry({
  userId,
  entryDate,
  metricKey,
  value,
  metricName,
  metricEmoji,
  unit,
}: {
  userId: string;
  entryDate: string;
  metricKey: string;
  value: number | boolean;
  metricName: string;
  metricEmoji: string;
  unit?: string;
}): Promise<void> {
  const time = formatTime(new Date());
  
  let valueText: string;
  if (typeof value === "boolean") {
    valueText = value ? "Yes" : "No";
  } else {
    valueText = unit ? `${value} ${unit}` : `${value}`;
  }
  
  const entryText = `Logged ${valueText} ${metricName} at ${time}`;
  
  await logDetailedEntry({
    userId,
    entryDate,
    metricKey,
    entryText,
  });
}

/**
 * Logs AI-parsed entries with item breakdowns
 * Format: "ITEM -> (+/-/set) VALUE METRIC_NAME at TIME"
 */
export async function logAIQuickEntry({
  userId,
  entryDate,
  metricKey,
  value,
  metricName,
  metricEmoji,
  unit,
  item,
  operation,
}: {
  userId: string;
  entryDate: string;
  metricKey: string;
  value: number | boolean;
  metricName: string;
  metricEmoji: string;
  unit?: string;
  item?: string;
  operation?: "add" | "subtract" | "set";
}): Promise<void> {
  const time = formatTime(new Date());
  
  let valueText: string;
  if (typeof value === "boolean") {
    valueText = value ? "Yes" : "No";
  } else {
    valueText = unit ? `${value} ${unit}` : `${value}`;
  }
  
  let entryText: string;
  
  if (item) {
    // Item breakdown format with operation
    const operationSymbol = operation === "add" ? "+" : operation === "subtract" ? "-" : "set";
    entryText = `${item} → ${operationSymbol} ${valueText} ${metricName} at ${time}`;
  } else {
    // No item breakdown, use simple format
    entryText = `Logged ${valueText} ${metricName} at ${time}`;
  }
  
  await logDetailedEntry({
    userId,
    entryDate,
    metricKey,
    entryText,
  });
}

/**
 * Fetches detailed entries for a specific date and metric
 * Returns entries sorted newest to oldest
 */
export async function getDetailedEntries({
  userId,
  entryDate,
  metricKey,
}: {
  userId: string;
  entryDate: string;
  metricKey: string;
}): Promise<
  Array<{
    id: string;
    entry_text: string;
    entry_time: string;
  }>
> {
  try {
    const { data, error } = await supabase
      .from("detailed_entries")
      .select("id, entry_text, entry_time")
      .eq("user_id", userId)
      .eq("entry_date", entryDate)
      .eq("metric_key", metricKey)
      .order("entry_time", { ascending: false })
      .limit(25);

    if (error) {
      console.error("[DetailedEntryLogger] Failed to fetch entries:", error);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error("[DetailedEntryLogger] Exception fetching entries:", err);
    return [];
  }
}
